import React, { useMemo, useCallback, useState } from "react";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

export const COLORS = [
    "#566374",
    "#28A745",
    "#1976D2",
    "#22B2D4", // rgb(34, 178, 212)
    "#DC3545",
    "#0A83A1",
    "#045266",
    "#E4D354", // rgb(228, 211, 84)
];

const nameMap = {
    car: "Sedan",
    suv: "SUV",
    other: "Others",
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
        const nameKey = payload[0].name.toLowerCase();
        const displayName = nameMap[nameKey] || (payload[0].name.charAt(0).toUpperCase() + payload[0].name.slice(1));

        return (
            <div style={tooltipStyles}>
                <strong>{displayName}</strong>
                <br />
                <strong>{Number(payload[0].value).toLocaleString()}</strong>
            </div>
        );
    }
    return null;
};

const tooltipStyles = {
    backgroundColor: "white",
    color: "black",
    border: "1px solid #ccc",
    padding: 6,
    borderRadius: 4,
    transition: "opacity 0.3s ease",
};

const ChartFleetSize = ({ data }) => {
    const [activeIndex, setActiveIndex] = useState(null);

    const colorMap = useMemo(() => {
        const map = {};
        data.forEach((item, index) => {
            map[item.name] = COLORS[index % COLORS.length];
        });
        return map;
    }, [data]);

    const top4Names = useMemo(() =>
        [...data].sort((a, b) => b.value - a.value).slice(0, 4).map(item => item.name),
        [data]);

    const renderLabel = useCallback(({ name }) => {
        return top4Names.includes(name) ? name.toUpperCase() : '';
    }, [top4Names]);

    const renderCustomLabelLine = useCallback((props) => {
        const { cx, cy, outerRadius, midAngle, stroke, payload } = props;
        const name = payload.name;
        if (!top4Names.includes(name)) return null;

        const RADIAN = Math.PI / 180;
        const x = cx + (outerRadius + 18) * Math.cos(-midAngle * RADIAN);
        const y = cy + (outerRadius + 18) * Math.sin(-midAngle * RADIAN);

        return <path d={`M${cx},${cy}L${x},${y}`} stroke={stroke} fill="none" />;
    }, [top4Names]);

    const handleMouseEnter = useCallback((_, index) => {
        setActiveIndex(index);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setActiveIndex(null);
    }, []);

    return (
        <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        outerRadius={110}
                        label={renderLabel}
                        labelLine={renderCustomLabelLine}
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {data.map((entry, index) => {
                            const isActive = activeIndex === index;
                            const dimOthers = activeIndex !== null && !isActive;
                            const fill = colorMap[entry.name] || "#ccc";

                            return (
                                <Cell
                                    key={entry.name || `cell-${index}`}
                                    fill={fill}
                                    style={{
                                        opacity: dimOthers ? 0.3 : 1,
                                        transition: "opacity 0.3s ease",
                                        cursor: "pointer"
                                    }}
                                />
                            );
                        })}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ChartFleetSize;
